/**
 * PHÂN MỨC CẢNH BÁO — nguồn duy nhất cho dải "Nguy" ở khung ứng dụng.
 *
 * Đặc tả: `31_QUY_HOACH_APP.md` PHẦN 2 luật 3 (ba mức = ba hình dạng) và PHẦN 5B mục 3
 * (dải mức Nguy dính dưới thanh trên, **chỉ mức 1**).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * 🔴 HÔM NAY KHÔNG CÓ CỘT `severity` Ở BẤT KỲ ĐÂU
 *
 * Đã đo: `Alarm` trong `schema.prisma` có `code · output · active · message · raisedAt`
 * — **không có mức**. Nên mức phải suy ra từ `code`, và chỗ suy ra đó phải là **một chỗ**,
 * không phải mỗi màn hình tự đoán một kiểu.
 *
 * ⚠ Phần lớn bảng này là **suy luận**, không phải phép đo: nói "OVER_VOLTAGE là mức 1" là tôi
 *   đọc câu *"Bảo vệ CẮT ngõ ra do QUÁ ÁP"* trong `server/src/gateway/canhbao.ts` rồi đối chiếu
 *   với định nghĩa mức 1 của lee — *"đang cắt điện / mất bảo vệ"*.
 *
 * ✅ Ba mã tôi từng đánh dấu là **không chắc** nay đã có phép đo của dev_device (YC-27):
 *      `METER_TIMEOUT`  mức 1 — tôi đúng, và lý do mạnh hơn tôi viết
 *      `RTC_INVALID`    mức 1 — **đổi từ mức 2**
 *      `CONTACTOR_FAIL` **chưa xếp được** — không ai dựng nổi nó ⇒ `chuaCoNguon`
 *
 * 📌 **Luật dev_device rút ra, áp cho cả bảng:** *mức phân theo cái người trực NHÌN THẤY, không
 *    theo cơ chế gây ra.* "Bảo vệ cắt" và "lịch chạy sai giờ" khác nguyên nhân, **giống hệt nhau
 *    ở hậu quả — một con đường tối**.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 VÀ MỘT CHỖ LỆCH ĐO ĐƯỢC, KHÔNG PHẢI SUY:
 *
 * `deviceGateway.ts:1030` `onKappAlarm()` ghi `code: String(a?.code ?? 'UNKNOWN')`, trong khi
 * `codec.ts:70` giải mã bằng `enums: Number`. ⇒ cảnh báo do **tủ v2 đẩy lên** vào CSDL dưới
 * dạng **chuỗi chữ số** (`"4"`), còn cảnh báo do **máy chủ tự dựng** thì là tên (`"OVER_VOLTAGE"`).
 * Cùng một sự việc, hai cách viết.
 *
 * Nên `chuanHoaMa()` phải nhận **cả hai**. Đây là **vá ở chỗ đọc**, không phải chỗ chữa —
 * chỗ chữa nằm ở máy chủ và tôi đã nêu ra thay vì lặng lẽ sửa.
 */

/** Mức 1 = Nguy · Mức 2 = Cảnh báo. *(Mức 3 "chưa rõ" không phải một cảnh báo — nó là VẮNG số liệu.)* */
export type MucCanhBao = 1 | 2;

interface DienGiai {
  mucDo: MucCanhBao;
  /** Câu người trực ca đọc được, không phải mã. */
  nhan: string;
  /** Vì sao xếp mức này — để lần sau ai đổi thì phải đối lại lý do, không đổi theo cảm giác. */
  viSao: string;
  /**
   * 🔴 **KHÔNG CÓ ĐƯỜNG NÀO DỰNG ĐƯỢC MÃ NÀY HÔM NAY.** Mức ghi ở trên là mức **dự kiến**
   * cho ngày có nguồn — không phải một thứ hệ thống đang canh.
   *
   * dev_device, YC-27 mục 3(a): *"Một mã cảnh báo nằm trong bảng phân mức, có nhãn tiếng Việt
   * tử tế, có mức 1 — mà không có đường nào dựng được nó — thì nó tạo đúng cảm giác an toàn
   * sai: người đọc bảng tin rằng hệ thống CÓ canh chuyện đó. Nó không canh."*
   *
   * ⇒ Cùng một luật với hình icon tạm ở PHẦN 5A: **một thứ trông hợp lý thì không bao giờ
   *   được sửa.** Giữ khoá để bảng vẫn khớp đủ 11 mã của `.proto`, nhưng đánh dấu ra mặt.
   *
   * ⚠️ **Dấu này KHÔNG phải một kết luận vĩnh viễn.** `viSao` phải nói rõ **đang chờ cái gì** —
   *   nếu không, ba tháng sau người đọc hiểu thành *"mã này không bao giờ dựng được"*, và đó
   *   lại đúng cái cảm giác sai mà dấu này sinh ra để chống. Xem `NVS_ERROR`: nó chờ **một
   *   trường trên dây**, không chờ ai đổi API — hai câu rất khác nhau.
   */
  chuaCoNguon?: true;
}

/**
 * `AlarmCode` trong `proto/kapp_v2.proto:186`. Tủ đẩy lên bằng SỐ; bảng này dịch số về tên
 * để phần còn lại của app chỉ phải biết một cách viết.
 *
 * ⚠ Giữ đúng thứ tự và đúng giá trị của `.proto` — nguồn sự thật là kho Kapp, không phải đây.
 */
export const TEN_THEO_SO: Record<number, string> = {
  1: 'PHASE_LOSS',
  2: 'OVER_CURRENT',
  3: 'UNDER_VOLTAGE',
  4: 'OVER_VOLTAGE',
  5: 'CONTACTOR_FAIL',
  6: 'METER_TIMEOUT',
  7: 'RTC_INVALID',
  8: 'GPS_LOST',
  9: 'NVS_ERROR',
  10: 'DOOR_OPEN',
  11: 'SCHEDULE_MISMATCH',
};

/**
 * BẢNG PHÂN MỨC. Khoá là tên đã chuẩn hoá (bỏ tiền tố `ALARM_`).
 *
 * Mức 1 theo đúng định nghĩa lee đưa: **đang cắt điện, hoặc mất bảo vệ.**
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 LUẬT YC-47 — **APP NÓI VỀ SỐ ĐO, KHÔNG NÓI VỀ HÀNH ĐỘNG CỦA TỦ.**
 *
 * dev_device chốt 08/09, nguyên văn: *"App được phép nói về SỐ ĐO nó nhận được. App KHÔNG được
 * nói về một HÀNH ĐỘNG của tủ (đã cắt · đã đóng · đã khoá · đã trả quyền) trừ khi có trường của
 * chính hành động ấy từ tủ."*
 *
 * ⛔ BA NHÃN NÀY TỪNG VI PHẠM, và nó đẻ ra đúng cái ảnh lee chụp: dải đỏ *"Thấp áp — bảo vệ đã
 *   CẮT ngõ ra"* lúc 10:22 sáng. Ba nhân chứng độc lập nói **không có lần cắt nào**:
 *
 *     `uvSetV = 170 V`   (tủ khai, đo được trong nhật ký 08/09 03:36:41) ⇒ 189 V KHÔNG dưới ngưỡng
 *     `protectEn = 0x00` (tủ khai) ⇒ bảo vệ quá/thấp áp ĐANG TẮT, tủ không thể cắt vì áp
 *     10:22 sáng, lặn 17:58 ⇒ ban ngày, lịch vốn tắt đèn — ba ngõ tắt là bình thường
 *
 *   Cái duy nhất "cắt" là **số cứng `190` của máy chủ** (đã bỏ, YC-48).
 *
 * 🔴 VÌ SAO NẶNG HƠN NGƯỠNG SAI: ngưỡng sai làm **kêu oan**; khẳng định một hành động không có
 *   làm **hỏng bản đồ nhân quả** của người trực — họ đi tìm nguyên nhân cho một lần cắt không
 *   tồn tại. Đúng cái giá đã chép vào sổ hôm qua.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * ⚠ VÀ VÌ SAO PHẢI SỬA Ở **NHÃN**, không phải ở câu của từng hàng: cùng một mã có HAI NGUỒN.
 *
 *     `UNDER_VOLTAGE` từ `ExceptionMessage` 9003  → TỦ TỰ KHAI đã cắt  → câu "đã CẮT" HỢP LỆ
 *     `UNDER_VOLTAGE` từ `checkMeterAlarms`       → máy chủ suy từ số  → câu "đã CẮT" là BỊA
 *
 *   Nhãn theo MÃ **không biết nguồn**, nên nó không được phép nói hành động. Hành động thuộc về
 *   `message` của từng hàng — nơi chỗ dựng biết mình đứng ở nguồn nào. Cùng kiến trúc `mucDo`
 *   theo hàng của YC-29: **bảng theo mã không biết sự thật theo hàng.**
 *
 * ✅ NGOẠI LỆ DUY NHẤT, và nó là ngoại lệ CÓ CƠ SỞ chứ không phải một chỗ trừ: `SAFETY_CUT` chỉ
 *   được dựng từ MỘT chỗ, và chỗ ấy đọc thẳng `DeviceLiveState.safetyCut` — tức chính trường
 *   hành động tủ khai. Mã ấy KHÔNG TỒN TẠI nếu tủ chưa nói nó đang cắt. Bộ kiểm đo đúng điều đó.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** Mã được phép nói HÀNH ĐỘNG trong nhãn — vì nó chỉ sinh ra từ trường hành động của tủ. */
export const MA_TU_KHAI_HANH_DONG: readonly string[] = ['SAFETY_CUT'];

/** Động từ hành động: thứ app không được tự khẳng định. Dùng cho cả bộ kiểm lẫn người đọc. */
export const DONG_TU_HANH_DONG = ['đã CẮT', 'đã cắt', 'đã đóng', 'đã khoá', 'đã trả quyền'];

export const BANG_MUC: Record<string, DienGiai> = {
  /* ---- mức 1: đang cắt điện ---- */
  OVER_VOLTAGE: {
    mucDo: 1, nhan: 'Điện áp CAO vượt ngưỡng của tủ',
    viSao: 'canhbao.ts mã 9002: "Bảo vệ CẮT ngõ ra do QUÁ ÁP" — đèn đang tắt vì bảo vệ',
  },
  UNDER_VOLTAGE: {
    mucDo: 1, nhan: 'Điện áp THẤP dưới ngưỡng của tủ',
    viSao: 'canhbao.ts mã 9003: "Bảo vệ CẮT ngõ ra do THẤP ÁP"',
  },
  OVER_CURRENT: {
    mucDo: 1, nhan: 'Dòng điện VƯỢT ngưỡng cảnh báo của tủ',
    viSao: 'TK-22 B: máy chủ tự xét i1..i3 so với ngưỡng cảnh báo đặt cho TỪNG tủ (trống = không xét) — '
         + 'CHỈ BÁO, không cắt. (Mã 9005 "Bảo vệ CẮT do QUÁ DÒNG" cùng mã này, nhưng firmware chưa gửi bao giờ.)',
  },
  PHASE_LOSS: {
    mucDo: 1, nhan: 'Mất pha',
    viSao: 'deviceGateway.ts checkMeterAlarms: một pha < 10V trong khi pha khác > 190V — pha đó đang mất điện',
  },
  CONTACTOR_FAIL: {
    mucDo: 1, nhan: 'Đã ra lệnh bật nhưng không có dòng điện — nghi contactor không đóng',
    viSao: 'dev_device đo 03/09: CHƯA CÓ NGUỒN. Trên dây không có trường dòng điện, và '
         + 'outputState chỉ là tiếng vọng của lệnh (kapp_v2_app.cpp:169) nên không lệch được. '
         + 'Chỉ bộ mô phỏng dựng được mã này. Mức 1 là mức DỰ KIẾN khi có nguồn. '
         + 'ĐANG CHỜ: một trường DÒNG ĐIỆN trên dây, HOẶC một tiếp điểm phụ contactor đọc thật. '
         + 'Gỡ dấu khi có một trong hai — outputState hiện nay không dùng được, nó là tiếng vọng của lệnh.',
    chuaCoNguon: true,
  },
  METER_TIMEOUT: {
    /*
     * ⚠ `mucDo: 1` ở đây nay chỉ là mức MẶC ĐỊNH khi hàng cảnh báo không mang mức riêng
     *   (dữ liệu cũ, trước YC-29). Mức thật đi kèm từng hàng qua `Alarm.mucDo`.
     * 🔴 Và NHÃN đã bỏ vế nhân quả: câu cũ *"tủ đang chạy KHÔNG có bảo vệ"* đúng về sự kiện
     *   nhưng khai một quan hệ *"mất công tơ ⇒ mất bảo vệ"* KHÔNG có thật trên một tủ đã
     *   `protectEn = 0`. Vế ấy nay nằm trong `message` của từng hàng, nơi biết `protectEn`.
     */
    mucDo: 1, nhan: 'Không đọc được công tơ',
    viSao: 'Firmware tự in ra câu ấy (bsp_dev_util.cpp:169): cổng an toàn HỎNG MỞ — mất công tơ '
         + 'thì tủ VẪN chạy lịch, chỉ là thôi bảo vệ. Nhưng nhánh ấy nằm SAU cổng protectEn '
         + '(bsp_dev_util.cpp:163): protectEn = 0 thì không có bảo vệ nào để mà mất, và câu '
         + 'cảnh báo phải nói khác (YC-29). Ba mức đi theo từng hàng, không theo mã.',
  },

  /*
   * ⛔ MÃ NÀY DO MÁY CHỦ DỰNG, không phải mã tủ tự phát — nên nó KHÔNG có số trong
   *   `TEN_THEO_SO`, y như phần `METER_TIMEOUT` dựng từ `DeviceHealth`.
   *
   * ✅ CÓ NGUỒN THẬT, và nguồn ấy vừa được đo trên PHẦN CỨNG (dev_device, 07/09/2026):
   *   `DeviceLiveState.safetyCut` + `.safetyCutBits`, tủ TỰ ĐẨY khi cổng an toàn đổi —
   *   5 khung `req_id = 0`, 4 x `cat=0` và 1 x `cat=1 bits=0x02`, không lệch cấp trong 40 phút.
   *   Trước phép đo ấy, mở theo MỨC sẽ kẹt mở vĩnh viễn; nên nó chỉ được dựng SAU, không trước.
   *
   * ⛔ Câu chữ KHÔNG được nói "đã khoá": cổng quá dòng (bit3 `0x08`) khoá cứng sau 3 lần thử,
   *   nhưng SỐ LẦN THỬ không có trên dây. Nói "đã khoá" là kết luận từ một thứ không đo được.
   */
  SAFETY_CUT: {
    mucDo: 1, nhan: 'Bảo vệ đang CẮT ngõ ra — đèn tắt do bảo vệ, không do lịch',
    viSao: 'Đúng định nghĩa mức 1 của lee: đang cắt điện. Đèn trên tuyến tắt vì bảo vệ, không '
         + 'vì lịch. Lớp cắt đọc từ safetyCutBits (cùng sơ đồ bit với protectEn: bit0 chống rò '
         + '· bit1 quá áp · bit2 thấp áp · bit3 quá dòng). bits = 0 vẫn HỢP LỆ — tủ đang cắt mà '
         + 'không khai được lớp — và câu chữ phải nói ra chuyện đó thay vì im.',
  },

  /* ---- mức 2: lệch chuẩn, chưa cắt ---- */
  SCHEDULE_MISMATCH: {
    mucDo: 2, nhan: 'Lịch trên tủ lệch so với máy chủ',
    viSao: 'Đèn vẫn chạy, chỉ là chạy theo lịch khác — sai giờ, không mất điện',
  },
  RTC_INVALID: {
    mucDo: 1, nhan: 'Đồng hồ trong tủ sai giờ — đèn có thể bật/tắt lệch giờ. Bấm “Đồng bộ giờ”',
    viSao: 'ĐỔI TỪ MỨC 2 theo YC-27. dev_device đo: bsp_time_is_trusted() có ở bsp_time.cpp:213 '
         + 'và KHÔNG một chỗ nào gọi nó; bộ lịch đọc thẳng wb->cur_time vô điều kiện. '
         + 'Giờ sai ⇒ 48 timer so với giờ sai ⇒ cả tuyến tối suốt đêm. Mức phân theo cái người '
         + 'trực NHÌN THẤY, không theo cơ chế: một con đường tối.',
  },
  GPS_LOST: {
    mucDo: 2, nhan: 'Mất định vị GPS',
    viSao: 'Chỉ ảnh hưởng giờ mặt trời mọc/lặn tính theo toạ độ; tủ vẫn có lịch cố định để chạy. '
         + 'CÓ NGUỒN THẬT (NV56): máy chủ dựng từ DeviceHealth.gpsLink + gpsBadPermille — dữ liệu '
         + 'đã lên tới máy chủ ba tuần mà không ai nghe.',
  },
  NVS_ERROR: {
    mucDo: 2, nhan: 'Tủ đang chạy lịch MẶC ĐỊNH — không phải lịch của bạn',
    viSao: 'ĐÃ CÓ NGUỒN (NV57, gỡ chuaCoNguon 03/09): DeviceHealth.nvsDefaultedKeys là bitmask '
         + 'khoá NVS bị thay bằng mặc định — bit0 device_config · bit1 contactors · bit2 schedule '
         + '· bit3 network_config. Máy chủ dựng ở onKappHealth. Nguồn có sẵn từ trước: '
         + 'readStorage() ở main.cpp:118-120 đã tính ba biến *_valid rồi VỨT ĐI; NV57 chỉ đưa '
         + 'chúng đi. ⛔ SCHEDULE_MISMATCH KHÔNG nổ thay — mã 9001 chỉ dựng khi gói lịch v1 ghi '
         + 'đè cấu hình từng ngõ, nguyên nhân khác hẳn.',
  },
  DOOR_OPEN: {
    mucDo: 2, nhan: 'Cửa tủ đang mở',
    viSao: 'dev_device đo 03/09 (NV56): CHƯA CÓ NGUỒN. grep "door|cua_tu" toàn firmware ⇒ 0 dòng. '
         + 'Không cảm biến, không chân vào, không gì cả. ĐANG CHỜ: phần cứng — một công tắc cửa '
         + 'và một chân GPIO. Đây là mã DUY NHẤT trong ba mã cần thêm VẬT LIỆU, không phải thêm mã. '
         + 'Gỡ dấu khi có tủ đời mới lắp công tắc cửa.',
    chuaCoNguon: true,
  },
  MAT_KET_NOI: {
    mucDo: 2, nhan: 'Mất kết nối với máy chủ — đèn chạy theo lịch trong tủ, máy chủ không thấy trạng thái',
    viSao: 'TK-25 (hình lee duyệt 10/10): tủ im là một VIỆC trong ô "Vấn đề cần xem xét xử lý". Mức 2, không 1: '
         + 'mức 1 kéo dải đỏ + popup cho mọi tài khoản, và tủ 4G chập chờn sẽ treo dải ấy cả ngày (tệp 29 '
         + 'L-NGÂN-SÁCH-MÀU: mất kết nối = xám). Máy chủ dựng ở vòng quét offline, tự đóng khi tủ nối lại.',
  },
  DEVICE_ANNOUNCE: {
    mucDo: 2, nhan: 'Tủ tự báo lỗi',
    viSao: 'deviceGateway.ts onAnnounceError: nội dung là chuỗi TỰ DO của tủ. Không đọc được nội dung thì không được tự phong mức 1',
  },
};

/**
 * Đưa mọi cách viết về một tên: `"4"` · `4` · `"ALARM_OVER_VOLTAGE"` · `"over_voltage"`
 * → `"OVER_VOLTAGE"`.
 */
export function chuanHoaMa(code: string | number | null | undefined): string {
  if (code === null || code === undefined) return '';
  const raw = String(code).trim();
  if (raw === '') return '';
  // Tủ v2 đẩy lên dạng số (codec dùng `enums: Number`).
  if (/^\d+$/.test(raw)) return TEN_THEO_SO[Number(raw)] ?? raw;
  return raw.toUpperCase().replace(/^ALARM_/, '');
}

export interface KetQuaPhanMuc {
  mucDo: MucCanhBao;
  nhan: string;
  /** Tên đã chuẩn hoá — dùng để hiện kèm nhãn, và để người đọc đối chiếu với `.proto`. */
  ma: string;
  /**
   * 🔴 `false` = **mã này không có trong bảng**. Không phải "an toàn", là "chưa biết".
   * Chỗ vẽ phải nói ra, đừng lẳng lặng vẽ như một cảnh báo bình thường.
   */
  biet: boolean;
  /**
   * 🔴 `true` = mã **không có đường nào dựng được** tính tới hôm nay. Nếu nó vẫn xuất hiện
   * trong dữ liệu thật thì **một trong hai điều đã đổi mà không ai ghi lại** — hoặc firmware
   * bắt đầu gửi, hoặc một bộ mô phỏng đang chạy vào CSDL thật. Cả hai đều đáng dừng lại xem.
   *
   * `npm run kiem-duong` quét dữ liệu sống và **ĐỎ** nếu bắt được mã như vậy.
   */
  chuaCoNguon: boolean;
}

/**
 * ⛔ MÃ LẠ KHÔNG ĐƯỢC LÀ MỨC 1.
 *
 * Không phải vì mã lạ thì nhẹ — mà vì dải mức 1 **luôn hiện, không cuộn mất**. Một mã lạ
 * hay gặp sẽ làm dải đó thường trực, và lee đã chốt: *"một dải luôn hiện thì mắt thôi nhìn
 * nó."* Dải mất tác dụng thì cảnh báo THẬT cũng chìm theo — cùng đúng lý do
 * `dienGiaiMaLoi()` bên máy chủ từ chối dựng cảnh báo cho mã ngoài dải 9001–9005.
 *
 * Đổi lại, `biet: false` bắt chỗ vẽ phải nói "chưa phân mức" ra mặt.
 */
/**
 * `mucDoRieng` — MỨC CỦA LẦN NÀY, do máy chủ ghi vào `Alarm.mucDo` (YC-29).
 *
 * 🔴 Vì sao mức không còn sống trọn trong `BANG_MUC`: `METER_TIMEOUT` có BA mức tuỳ
 *   `protectEn` của tủ — mức 1 khi bảo vệ đang bật (mất bảo vệ thật), mức 2 khi bảo vệ VỐN
 *   đã tắt, mức 3 khi máy chủ chưa đọc được `protectEn`. Cùng một mã, ba mức.
 *
 * ⛔ `null`/`undefined` = *"lần này không có mức riêng"* ⇒ đọc `BANG_MUC` như cũ. KHÔNG
 *   được đọc thành mức 0 hay mức mặc định nào — vắng ≠ 0, lần thứ n.
 * ⚠ Chỉ nhận 1 · 2 · 3. Một con số lạ từ CSDL (firmware/máy chủ mới hơn web) rơi về bảng
 *   chứ không đi thẳng vào giao diện: cùng khuôn `docMaAlarm()`.
 */
export function phanMuc(
  code: string | number | null | undefined,
  mucDoRieng?: number | null,
): KetQuaPhanMuc {
  const ma = chuanHoaMa(code);
  const g = BANG_MUC[ma];
  /*
   * ⚠ CHỈ 1 và 2. `MucCanhBao` cố ý không có mức 3 (xem chú thích ở khai báo kiểu): "chưa rõ"
   *   là VẮNG SỐ LIỆU, không phải một mức của một cảnh báo đang mở. Một số 3 lọt từ CSDL
   *   *(máy chủ mới hơn web)* rơi về bảng, không đi thẳng vào giao diện — cùng khuôn
   *   `docMaAlarm()`.
   */
  const rieng = mucDoRieng === 1 || mucDoRieng === 2 ? mucDoRieng : undefined;
  if (g) {
    return {
      mucDo: rieng ?? g.mucDo, nhan: g.nhan, ma, biet: true,
      chuaCoNguon: g.chuaCoNguon === true,
    };
  }
  return { mucDo: rieng ?? 2, nhan: ma || 'Cảnh báo không rõ mã', ma, biet: false, chuaCoNguon: false };
}

/** Các mã đang đánh dấu `chuaCoNguon`. `kiem-duong` dùng để quét dữ liệu sống. */
export function maChuaCoNguon(): string[] {
  return Object.entries(BANG_MUC).filter(([, g]) => g.chuaCoNguon === true).map(([k]) => k);
}

/** Đúng những cảnh báo được phép chiếm dải dính trên cùng. */
export function locMucNguy<T extends { code: string; mucDo?: number | null }>(ds: T[] | undefined): T[] {
  return (ds ?? []).filter((a) => phanMuc(a.code, a.mucDo).mucDo === 1);
}

/* ------------------------------------------- ĐỘ TƯƠI CỦA CẢNH BÁO (YC-44) */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * CÂU NÓI THÊM KHI CẢNH BÁO KHÔNG CÒN ĐƯỢC ĐO LẠI — lee, 07/09: *"lỗi gì sao tôi không tắt
 * được"*, trên một tủ đang khoẻ, đèn đang sáng.
 *
 * 🔴 Nó KHÔNG làm cảnh báo tắt đi, và đó là chủ ý: đóng hộ tủ là **bịt miệng nguồn tin duy
 *   nhất** (dev_device chốt, lối C bị loại). Nó làm cảnh báo **thôi khẳng định** — chuyển từ
 *   *"chuyện này ĐANG xảy ra"* sang *"lần đo cuối nói vậy, và đã lâu không đo lại được"*.
 *
 * ⛔ Ba ca, ba câu, không gộp:
 *     `moi`               → không nói gì thêm; cảnh báo đang được đo lại đều
 *     `qua-han`           → có mốc giờ ⇒ nói ra mốc ấy, để người trực biết đi xem từ đâu
 *     `chua-do-lan-nao`   → KHÔNG có mốc nào ⇒ tuyệt đối không bịa ra một giờ
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function cauDoTuoi(
  doTuoi: { tuoi: string; lucNhanMs?: number } | null | undefined,
  gioVN: (ms: number) => string,
): string | null {
  if (!doTuoi || doTuoi.tuoi === 'moi') return null;
  if (doTuoi.tuoi === 'chua-do-lan-nao') {
    return 'máy chủ CHƯA đo lại được lần nào — đây là lần đo cũ, không phải tình trạng hiện tại';
  }
  return typeof doTuoi.lucNhanMs === 'number'
    ? `chưa đo lại được từ ${gioVN(doTuoi.lucNhanMs)} — đây là lần đo cũ`
    : 'chưa đo lại được — đây là lần đo cũ';
}

/* ------------------------------------------- MÃ TỰ TẮT vs MÃ CHỜ NGƯỜI (YC-49) */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 CÂU *"Cảnh báo này KHÔNG tự tắt"* NAY SAI VỚI PHẦN LỚN MÃ — dev_device bắt 08/09.
 *
 * Nó là một câu **nói cho mọi mã mức 1**, viết khi mọi cảnh báo đều phải có người xác nhận.
 * Từ NV92 tủ **tự khai khi nhả cổng an toàn** (`bsp_dev_util.cpp:262-264` → `safetyCut = 0`
 * → tủ đẩy `DeviceLiveState`), và máy chủ đóng cảnh báo theo MỨC.
 *
 * ⇒ Cùng loại lỗi cả ba bên vấp suốt tuần: **một câu đúng lúc viết, hết hạn ở lần nạp kế tiếp.**
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * ⛔ VÀ NÓ TRỘN HAI THỨ KHÁC HẲN NHAU (dev_device tách, tôi chép lại nguyên bảng):
 *
 *      "đang cắt"    nguồn: `safetyCut` + bit  ·  MỨC     ·  tủ tự mở, tủ tự đóng  ·  dải, tự tắt
 *      "đã từng cắt" nguồn: sự kiện tại mốc    ·  BẢN GHI ·  cần người xác nhận     ·  Nhật ký
 *
 * Gộp lại thì dải vừa không tắt được, vừa nói *"cần xử lý ngay"* trong khi tủ sẽ tự đóng sau
 * 60 giây điện ổn — **không có việc gì cho người trực**. Một cảnh báo kêu khi không có việc để
 * làm sẽ bị phớt lờ, rồi **lần thật nó cũng bị phớt lờ**.
 *
 * ⚠ BẢNG NÀY LÀ BẢN SAO THỨ HAI, cố ý: web không nhập được từ máy chủ. Bộ kiểm so hai bảng
 *   với nhau VÀ so với các chỗ `dongCanhBao()` thật trong gateway — hai chiều, không phải một
 *   lời hứa giữ đồng bộ. Cùng khuôn `nguonthaotac.ts`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export const MA_TU_TAT: readonly string[] = [
  'GPS_LOST', 'MAT_KET_NOI', 'METER_TIMEOUT', 'NVS_ERROR', 'OVER_CURRENT', 'OVER_VOLTAGE', 'PHASE_LOSS',
  'RTC_INVALID', 'SAFETY_CUT', 'SCHEDULE_MISMATCH', 'UNDER_VOLTAGE',
];

export function maTuTat(code: string): boolean {
  return MA_TU_TAT.includes(code);
}

/**
 * TK-25 — bấm "Đã xử lý" có ĐÓNG hồ sơ không. `true` = KHÔNG đóng: chỉ ghi tên người bấm + giờ, hồ sơ chờ
 *   tủ/máy chủ thấy hết rồi tự đóng (hai nhân chứng). Mã do tủ v2 đẩy lên nằm ở dạng chữ số và chính tủ gửi
 *   `active = false` ⇒ cũng chờ. Luật gốc ở máy chủ `services/xulyviec.ts` `quyetDinhXuLy()`; bộ kiểm so hai
 *   bên trên MỌI mã của `BANG_MUC` + 1..11 — đây là bản để giao diện nói đúng câu TRƯỚC khi bấm.
 */
export function xuLyChoTuXacNhan(code: string): boolean {
  return /^\d+$/.test(code) || maTuTat(code);
}

/**
 * Câu chân popup. **Hai câu, không gộp** — chúng bảo người trực hai việc khác nhau.
 *
 * ⛔ Mã tự tắt: KHÔNG nói *"cần xử lý ngay"*. Nói đúng cái đang xảy ra và cái sẽ tự hết. Lần
 *   bấm vẫn vào nhật ký, vì *"đã từng xảy ra"* là một bản ghi đáng giữ — nhưng nó là bản ghi,
 *   không phải một việc phải làm.
 */
export function cauChanPopup(code: string): string {
  return maTuTat(code)
    ? 'Cảnh báo này TỰ TẮT khi tủ báo đã hết — bấm xác nhận chỉ để ghi vào nhật ký rằng bạn đã thấy.'
    : 'Mức 1 — cần xử lý ngay. Cảnh báo này KHÔNG tự tắt; lần bấm xác nhận được ghi vào nhật ký.';
}
